/**
 * Where a gallery's cards go.
 *
 * A gallery is a grid of cards rather than a list of rows, so virtualising it
 * needs one more number than a grid does: how many cards fit across. Everything
 * else follows from that, and all of it is arithmetic -- so it is here, pure,
 * rather than measured out of the DOM where no test can see it.
 *
 * The cards are a fixed size on purpose. A card whose height depended on its
 * contents could not be placed without laying out every card above it, which is
 * the thing virtualisation exists to avoid; the fields a card shows are the same
 * for every row in the view, so a height computed from the view is right for all
 * of them.
 */

/** The narrowest a card may be drawn before the row holds one fewer. */
export const MIN_CARD_WIDTH = 220
export const CARD_GAP = 12

/** The parts of a card, in the order they stack. */
export const COVER_HEIGHT = 128
export const CARD_PADDING = 12
export const CARD_TITLE_HEIGHT = 24
export const CARD_FIELD_HEIGHT = 40

export interface GalleryGeometry {
  /** How many cards fit across, never fewer than one. */
  perRow: number
  /** What each card is drawn at, gaps taken out of the container's width. */
  cardWidth: number
  cardHeight: number
  /**
   * How many pixels of scroll one *row of the view* consumes.
   *
   * The row window thinks in rows and asks for a range of them, so this is what
   * it is told a row costs: a row of cards is `cardHeight + CARD_GAP` tall and
   * holds `perRow` of them, so one row of the view is that divided by `perRow`.
   * Told the card's own height instead, the window would fetch `perRow` times
   * too few rows and the gallery would run out of cards halfway down the screen.
   */
  scrollPerRow: number
  /** The height the whole gallery would be if every row were drawn. */
  totalHeight: number
}

export function galleryGeometry({
  width,
  count,
  fieldCount,
  hasCover,
}: {
  width: number
  count: number
  fieldCount: number
  hasCover: boolean
}): GalleryGeometry {
  const perRow = Math.max(1, Math.floor((width + CARD_GAP) / (MIN_CARD_WIDTH + CARD_GAP)))
  const cardWidth = Math.max(0, (width - CARD_GAP * (perRow - 1)) / perRow)

  // The title is the primary field and is always drawn; the rest are the other
  // fields the view shows, each a label and a value.
  const cardHeight =
    (hasCover ? COVER_HEIGHT : 0) +
    CARD_PADDING * 2 +
    CARD_TITLE_HEIGHT +
    Math.max(0, fieldCount - 1) * CARD_FIELD_HEIGHT

  const rows = Math.ceil(count / perRow)

  return {
    perRow,
    cardWidth,
    cardHeight,
    scrollPerRow: (cardHeight + CARD_GAP) / perRow,
    // No trailing gap under the last row: the gap belongs between rows.
    totalHeight: rows === 0 ? 0 : rows * (cardHeight + CARD_GAP) - CARD_GAP,
  }
}

/** Where one row's card sits, measured from the top left of the gallery. */
export function cardPosition(
  index: number,
  { perRow, cardWidth, cardHeight }: GalleryGeometry,
): { left: number; top: number } {
  return {
    left: (index % perRow) * (cardWidth + CARD_GAP),
    top: Math.floor(index / perRow) * (cardHeight + CARD_GAP),
  }
}
